import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SEO } from "../components/SEO";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { FileText } from "lucide-react";
import { API } from "../constants";

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQData();
  }, []);

  const fetchFAQData = async () => {
    try {
      const [faqsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API}/faqs`),
        axios.get(`${API}/faq-categories`)
      ]);
      
      setFaqs(faqsResponse.data);
      setCategories(categoriesResponse.data.categories);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const filteredFaqs = selectedCategory 
    ? faqs.filter(faq => faq.category === selectedCategory)
    : faqs;

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading FAQs...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <SEO title="Frequently Asked Questions" description="Find answers about our printing services, turnaround times, artwork requirements, pricing, and more." path="/faq" />
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
        <p className="text-xl text-gray-600">Everything you need to know about our printing services</p>
      </div>

      {categories.length > 0 && (
        <div className="mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={selectedCategory === '' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('')}
              size="sm"
            >
              All Categories
            </Button>
            {categories.map(category => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                size="sm"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Custom Quote FAQ - Always shown first */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <div className="flex items-start space-x-4">
            <FileText className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                What products require a concierge quote?
              </h3>
              <p className="text-blue-800 leading-relaxed mb-4">
                For complex orders or specialty products, we offer personalized consultation through our Concierge Quote Service. Products requiring custom quotes include:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-700">
                <div>
                  <h4 className="font-semibold mb-2">Specialty Printing:</h4>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Rigid Signs (Aluminum, Acrylic, Foam Core)</li>
                    <li>Adhesive Vinyl & Window Graphics</li>
                    <li>Photo Paper & Canvas Prints</li>
                    <li>Vehicle Wraps & Decals</li>
                    <li>Industrial Signage</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Custom Services:</h4>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Multi-location Installations</li>
                    <li>Rush Orders (24-48 hours)</li>
                    <li>Color Matching & Pantone Colors</li>
                    <li>Large Volume Discounts (500+ pieces)</li>
                    <li>Custom Packaging & Fulfillment</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4">
                <Button asChild variant="default" size="sm">
                  <Link to="/concierge-quote">Request Concierge Quote</Link>
                </Button>
              </div>
              <Badge className="mt-3 bg-blue-200 text-blue-800">
                Custom Products
              </Badge>
            </div>
          </div>
        </Card>

        {filteredFaqs.map((faq, index) => (
          <Card key={faq.id || `faq-${index}`} className="p-6">
            <div className="flex items-start space-x-4">
              <FileText className="w-6 h-6 text-orange-500 mt-1 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {faq.answer}
                </p>
                <Badge className="mt-3 bg-gray-100 text-gray-700">
                  {faq.category}
                </Badge>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-12 text-center">
        <Card className="p-8 bg-orange-50 border-orange-200">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Still have questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Can't find the answer you're looking for? Our team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild>
              <Link to="/request-call">Request a Call</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/design-services">Contact Us</Link>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Blog, Cart, and other components remain the same...
// [Include the remaining components from the previous version without changes]

// Blog Page

export { FAQ };
export default FAQ;
